import { useEffect, useState } from "react";
import { Loader2, MousePointerClick, Play, Smartphone, Tablet, Monitor, X } from "lucide-react";
import client from "@/lib/api";

const SECTION_LABELS = {
  hero: "Header",
  intro: "Intro / About",
  services: "Investment",
  comparison: "Us vs The Rest",
  reviews: "Reviews",
  packages: "Packages & Pricing",
  "ready-cta": "Ready to Get Started",
  "wve-app": "WVE App",
  "storytelling-page": "Storytelling Page (full read)",
  "guarantee-page": "Lifetime Guarantee Page (full read)",
  "viewing-website": "Viewing Website",
  guestbook: "Video Guestbook",
  "venue-video": "Venue Video",
  questions: "Questions",
};

const CLICK_LABELS = {
  whatsapp: "WhatsApp button",
  "reviews-website": "Reviews website link",
  "secure-my-date": "Secure My Date button",
  "accept-submitted": "Accept form submitted",
  "accept-modal-opened": "Accept form opened",
  "accept-form-started": "Accept form started",
  "accept-form-abandoned": "Accept form abandoned",
  "guestbook-anchor": "Guestbook (in-package link)",
  "comparison-storytelling": "Storytelling page link",
  "comparison-guarantee": "Lifetime guarantee page link",
  "delivery-example": "Delivery example link",
  "guestbook-example": "Guestbook example",
  "viewing-website-anchor": "Viewing website (in-page link)",
  "footer-logo": "Footer logo",
  "example-highlight": "Highlight films examples",
  "example-cinematic": "Cinematic films examples",
  "example-photography": "Photography portfolio",
  "app-store": "App Store badge",
  "google-play": "Google Play badge",
  "accept-terms-viewed": "T&Cs step reached",
  "accept-terms-completed": "T&Cs read to the end",
  "storytelling-page-viewed": "Storytelling page visited",
  "guarantee-page-viewed": "Lifetime guarantee page visited",
};

function clickLabel(name) {
  if (CLICK_LABELS[name]) return CLICK_LABELS[name];
  if (name.startsWith("select-package-"))
    return `Selected package: ${name.replace("select-package-", "").replace(/-/g, " ")}`;
  if (name.startsWith("video-play-")) return "Played venue video";
  return name;
}

export function fmtTime(secs) {
  if (!secs) return "0s";
  const m = Math.floor(secs / 60);
  const s = Math.round(secs % 60);
  return m ? `${m}m ${s}s` : `${s}s`;
}

const SOURCE_LABELS = {
  email: "via Email",
  whatsapp: "via WhatsApp",
  direct: "Direct link",
  google: "via Google",
  facebook: "via Facebook",
  instagram: "via Instagram",
  internal: "In-site",
};

function FunnelStrip({ funnel }) {
  if (!funnel) return null;
  const steps = [
    { key: "opened", label: "Form opened" },
    { key: "started", label: "Started filling" },
    { key: "submitted", label: "Submitted" },
  ];
  return (
    <div className="mb-8" data-testid="funnel-strip">
      <div className="text-[10px] tracking-overline uppercase text-[#8A8782] font-medium mb-3">
        Accept funnel
      </div>
      <div className="grid grid-cols-3 gap-2">
        {steps.map((s) => (
          <div key={s.key} className="bg-[#FAF9F6] border border-[#E5E3DB] p-3 text-center">
            <div className="font-display text-2xl text-[#1F1E1D]">{funnel[s.key]}</div>
            <div className="text-[10px] tracking-overline uppercase text-[#8A8782] font-medium mt-1">
              {s.label}
            </div>
          </div>
        ))}
      </div>
      {funnel.abandoned > 0 && (
        <div className="mt-2 text-xs text-[#8A6D1A] bg-[#CCBA56]/10 border border-[#CCBA56]/40 px-3 py-2" data-testid="funnel-abandoned-warning">
          ⚠ Started the accept form {funnel.abandoned}× without finishing, a hot lead worth a follow-up.
        </div>
      )}
    </div>
  );
}

function VisitTimeline({ viewers, window: win }) {
  if (!viewers?.length || !win?.start || !win?.end) return null;
  const start = new Date(win.start).getTime();
  const end = new Date(win.end).getTime();
  const span = end - start;
  if (span <= 0) return null;
  const dots = viewers
    .map((v) => {
      const t = new Date(v.first_seen).getTime();
      return { pct: Math.min(100, Math.max(0, ((t - start) / span) * 100)), v };
    });
  const fmt = (iso) => new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
  return (
    <div className="mb-8" data-testid="visit-timeline">
      <div className="text-[10px] tracking-overline uppercase text-[#8A8782] font-medium mb-3">
        Visit timeline (quote window)
      </div>
      <div className="relative h-8">
        <div className="absolute top-1/2 left-0 right-0 h-px bg-[#E5E3DB]" />
        {dots.map((d, i) => (
          <div
            key={i}
            title={`${d.v.device} · ${fmt(d.v.first_seen)}`}
            className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-2.5 h-2.5 rounded-full bg-[#A9927D] border-2 border-white shadow"
            style={{ left: `${d.pct}%` }}
          />
        ))}
      </div>
      <div className="flex justify-between text-[10px] text-[#8A8782] tracking-overline uppercase font-medium">
        <span>Sent {fmt(win.start)}</span>
        <span>Expires {fmt(win.end)}</span>
      </div>
    </div>
  );
}

function PackageAttention({ packages }) {
  if (!packages?.length) return null;
  const maxSecs = packages[0]?.seconds || 1;
  return (
    <div className="mb-8" data-testid="package-attention">
      <div className="text-[10px] tracking-overline uppercase text-[#8A8782] font-medium mb-3">
        Package attention (hover time)
      </div>
      <div className="space-y-2.5">
        {packages.map((p) => (
          <div key={p.name}>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-[#1F1E1D]">{p.name}</span>
              <span className="text-[#5C5A56] tabular-nums">{fmtTime(p.seconds)}</span>
            </div>
            <div className="h-1.5 bg-[#F3F1ED]">
              <div className="h-full bg-[#CCBA56]" style={{ width: `${Math.max(4, (p.seconds / maxSecs) * 100)}%` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function VideoEngagement({ videos }) {
  if (!videos?.length) return null;
  return (
    <div className="mb-8" data-testid="video-engagement">
      <div className="text-[10px] tracking-overline uppercase text-[#8A8782] font-medium mb-3">
        Venue video engagement
      </div>
      <div className="divide-y divide-[#F3F1ED]">
        {videos.map((v) => (
          <div key={v.name} className="flex items-center justify-between py-2.5 text-sm">
            <span className="flex items-center gap-2 text-[#1F1E1D]">
              <Play className="w-3.5 h-3.5 text-[#A9927D]" strokeWidth={1.5} />
              Video {v.name}
            </span>
            <span className="text-[#5C5A56] tabular-nums">
              {v.plays} play{v.plays === 1 ? "" : "s"} · {fmtTime(v.seconds)} watched
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function DaypartChart({ dayparts }) {
  if (!dayparts) return null;
  const parts = [
    { key: "morning", label: "Morning" },
    { key: "afternoon", label: "Afternoon" },
    { key: "evening", label: "Evening" },
    { key: "night", label: "Night" },
  ];
  const total = parts.reduce((a, p) => a + (dayparts[p.key] || 0), 0);
  if (!total) return null;
  const max = Math.max(...parts.map((p) => dayparts[p.key] || 0), 1);
  return (
    <div className="mb-8" data-testid="daypart-chart">
      <div className="text-[10px] tracking-overline uppercase text-[#8A8782] font-medium mb-3">
        When they read (UK time)
      </div>
      <div className="grid grid-cols-4 gap-2 items-end h-16">
        {parts.map((p) => (
          <div key={p.key} className="flex flex-col items-center justify-end h-full">
            <div className="text-xs text-[#5C5A56] tabular-nums mb-1">{dayparts[p.key] || 0}</div>
            <div
              className="w-full bg-[#A9927D]/70"
              style={{ height: `${Math.max(6, ((dayparts[p.key] || 0) / max) * 100)}%` }}
            />
          </div>
        ))}
      </div>
      <div className="grid grid-cols-4 gap-2 mt-1">
        {parts.map((p) => (
          <div key={p.key} className="text-center text-[10px] tracking-overline uppercase text-[#8A8782] font-medium">
            {p.label}
          </div>
        ))}
      </div>
    </div>
  );
}

function deviceIcon(device) {
  if (/iphone|android/i.test(device)) return Smartphone;
  if (/ipad|tablet/i.test(device)) return Tablet;
  return Monitor;
}

function fmtSeen(iso) {
  return new Date(iso).toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function ViewersList({ viewers, deviceCount }) {
  if (!viewers) return null;
  return (
    <div className="mb-8">
      <div className="text-[10px] tracking-overline uppercase text-[#8A8782] font-medium mb-3">
        Viewers &amp; devices{deviceCount > 0 ? ` (${deviceCount} distinct)` : ""}
      </div>
      {viewers.length === 0 ? (
        <div className="text-sm text-[#8A8782]">No visits recorded yet.</div>
      ) : (
        <div className="divide-y divide-[#F3F1ED]">
          {viewers.map((v, i) => {
            const Icon = deviceIcon(v.device);
            return (
              <div key={i} className="py-2.5" data-testid={`viewer-row-${i}`}>
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2 text-[#1F1E1D]">
                    <Icon className="w-3.5 h-3.5 text-[#A9927D]" strokeWidth={1.5} />
                    {v.device}
                    {v.source && (
                      <span className="text-[10px] tracking-overline uppercase text-[#A9927D] border border-[#A9927D]/40 px-1.5 py-0.5">
                        {SOURCE_LABELS[v.source] || v.source}
                      </span>
                    )}
                  </span>
                  <span className="text-[#5C5A56] tabular-nums">
                    {fmtTime(v.seconds)} reading · {v.clicks} click{v.clicks === 1 ? "" : "s"}
                    {v.depth > 0 && <span className="text-[#A9927D]"> · read {v.depth}%</span>}
                  </span>
                </div>
                <div className="text-xs text-[#8A8782] mt-0.5 pl-[22px]">
                  First seen {fmtSeen(v.first_seen)} · Last seen {fmtSeen(v.last_seen)}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function StatCards({ data }) {
  return (
    <div className="grid grid-cols-3 gap-4 mb-8">
      <div className="bg-[#FAF9F6] border border-[#E5E3DB] p-4 text-center">
        <div className="font-display text-3xl text-[#1F1E1D]">{data.views}</div>
        <div className="text-[10px] tracking-overline uppercase text-[#8A8782] font-medium mt-1">Visits</div>
      </div>
      <div className="bg-[#FAF9F6] border border-[#E5E3DB] p-4 text-center">
        <div className="font-display text-3xl text-[#1F1E1D]">{fmtTime(data.total_seconds)}</div>
        <div className="text-[10px] tracking-overline uppercase text-[#8A8782] font-medium mt-1">Time on page</div>
      </div>
      <div className="bg-[#FAF9F6] border border-[#E5E3DB] p-4 text-center">
        <div className="font-display text-lg text-[#1F1E1D] leading-tight pt-1.5">
          {data.last_viewed
            ? new Date(data.last_viewed).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })
            : "-"}
        </div>
        <div className="text-[10px] tracking-overline uppercase text-[#8A8782] font-medium mt-1">Last viewed</div>
      </div>
    </div>
  );
}

function SectionTimes({ sections }) {
  const maxSecs = sections?.[0]?.seconds || 1;
  return (
    <div className="mb-8">
      <div className="text-[10px] tracking-overline uppercase text-[#8A8782] font-medium mb-3">
        Time spent per section
      </div>
      {sections.length === 0 ? (
        <div className="text-sm text-[#8A8782]">No section data yet.</div>
      ) : (
        <div className="space-y-2.5">
          {sections.map((s) => (
            <div key={s.name} data-testid={`section-time-${s.name}`}>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-[#1F1E1D]">{SECTION_LABELS[s.name] || s.name}</span>
                <span className="text-[#5C5A56] tabular-nums">{fmtTime(s.seconds)}</span>
              </div>
              <div className="h-1.5 bg-[#F3F1ED]">
                <div
                  className="h-full bg-[#A9927D]"
                  style={{ width: `${Math.max(4, (s.seconds / maxSecs) * 100)}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ClickList({ clicks }) {
  return (
    <div>
      <div className="text-[10px] tracking-overline uppercase text-[#8A8782] font-medium mb-3">
        Links & buttons clicked
      </div>
      {clicks.length === 0 ? (
        <div className="text-sm text-[#8A8782]">No clicks recorded yet.</div>
      ) : (
        <div className="divide-y divide-[#F3F1ED]">
          {clicks.map((c) => (
            <div key={c.name} className="flex items-center justify-between py-2.5 text-sm" data-testid={`click-row-${c.name}`}>
              <span className="flex items-center gap-2 text-[#1F1E1D]">
                <MousePointerClick className="w-3.5 h-3.5 text-[#A9927D]" strokeWidth={1.5} />
                {clickLabel(c.name)}
              </span>
              <span className="text-[#5C5A56] tabular-nums">
                {c.count}× · {new Date(c.last_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ModalBody({ error, data }) {
  if (error) {
    return <div className="text-sm text-[#8A3D3D]">Failed to load analytics.</div>;
  }
  if (!data) {
    return (
      <div className="flex items-center gap-2 text-[#5C5A56] text-sm">
        <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} /> Loading…
      </div>
    );
  }
  return (
    <>
      <StatCards data={data} />
      <FunnelStrip funnel={data.funnel} />
      <VisitTimeline viewers={data.viewers} window={data.window} />
      <ViewersList viewers={data.viewers} deviceCount={data.device_count} />
      <PackageAttention packages={data.packages} />
      <SectionTimes sections={data.sections} />
      <VideoEngagement videos={data.videos} />
      <DaypartChart dayparts={data.dayparts} />
      <ClickList clicks={data.clicks} />
    </>
  );
}

export function AnalyticsModal({ quote, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    client
      .get(`/quotes/${quote.id}/analytics`)
      .then((r) => setData(r.data))
      .catch(() => setError(true));
  }, [quote.id]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-6"
      onClick={onClose}
      data-testid="analytics-modal"
    >
      <div
        className="bg-white w-full max-w-xl max-h-[85vh] overflow-y-auto p-8 md:p-10"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="text-[10px] tracking-overline uppercase text-[#A9927D] font-medium mb-1">
              Engagement Analytics
            </div>
            <div className="font-display text-3xl text-[#1F1E1D] leading-none">
              {quote.partner1_name} <span className="text-[#A9927D]">&</span>{" "}
              {quote.partner2_name}
            </div>
          </div>
          <button onClick={onClose} data-testid="analytics-close-btn" className="text-[#8A8782] hover:text-[#1F1E1D]">
            <X className="w-5 h-5" strokeWidth={1.5} />
          </button>
        </div>

        <ModalBody error={error} data={data} />
      </div>
    </div>
  );
}
